import { GetStaticPaths, GetStaticProps } from 'next';

import {
  BlogClosingCta,
  BlogFeed,
  CategoryHero,
} from '../../../components/blog/BlogListing';
import LandingPageLayout from '../../../components/layouts/LandingPageLayout';
import {
  blogCategories,
  BlogCategorySlug,
  getBlogCategory,
  postsInCategory,
} from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';
import { getCanonicalUrl } from '../../../utils/client';

type Props = {
  category: BlogCategorySlug;
};

const BlogCategoryPage = ({ category: categorySlug }: Props) => {
  const category = getBlogCategory(categorySlug);
  if (!category) {
    return null;
  }

  const posts = postsInCategory(category.slug);
  const path = `/c/blogs/${category.slug}`;
  const canonical = getCanonicalUrl(path);

  return (
    <LandingPageLayout
      title={`${category.name} | The Curipod Blog`}
      subTitle={category.description}
      disableHeadline
      canonicalPath={path}
      imageUrl={posts[0] ? getCanonicalUrl(posts[0].cover) : undefined}
      structuredData={[
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: category.name,
          description: category.description,
          url: canonical,
          isPartOf: {
            '@type': 'Blog',
            name: 'The Curipod Blog',
            url: getCanonicalUrl('/c/blogs'),
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: getCanonicalUrl('/'),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: getCanonicalUrl('/c/blogs'),
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: category.name,
              item: canonical,
            },
          ],
        },
      ]}
    >
      <CategoryHero name={category.name} description={category.description} />
      <BlogFeed active={category.slug} posts={posts} />
      <BlogClosingCta />
    </LandingPageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: blogCategories.map(category => ({ params: { category: category.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async context => {
  const category = context.params?.category;
  if (typeof category !== 'string' || !getBlogCategory(category)) {
    return { notFound: true };
  }

  return {
    props: {
      category: category as BlogCategorySlug,
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default BlogCategoryPage;
